import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
} from 'antd';

import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import API from 'utils/api';
import { onSuccess, onError } from 'utils/handlers';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';

const baseURI = 'tramites/familias-de-tramites-plantillas';

const TableFamiliaDelTramite = ({
  selectedRowKey,
  callback,
}) => {
  const [formTable] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [familia, setFamilia] = useState([]);
  const [subCatFam, setSubCatFam] = useState([]);
  const [subCatFamFiltered, setSubCatFamFiltered] = useState([]);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await API.get(baseURI, {
        params: {
          plantilla_de_tramite: selectedRowKey,
        },
      });

      if (response?.status === 200) {
        setData(response.data);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const resFam = await API.get('/tramites/familias-de-tramites/');
      setFamilia(resFam.data || []);
      const resSub = await API.get('/tramites/sub-categorias-de-familias-de-tramites/');
      setSubCatFam(resSub.data || []);
      if (selectedRowKey) {
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    fetchAll();
    return () => API.source.cancel;
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    formTable.resetFields();
    setVisibleAlert(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await formTable.validateFields();
      const values = formTable.getFieldsValue();
      const [key] = selectedRowKeys;
      if (selectedRowKey) {
        values.plantilla_de_tramite = selectedRowKey;
        if (!key) {
          const response = await API.post(baseURI, values);
          if (response?.status === 201) {
            onSuccess(response, 'Agregado correctamente');
            onCancel();
            await fetchData();
          }
        } else {
          const response = await API.put(`${baseURI}/${key}/`, values);
          if (response?.status === 200) {
            onSuccess(response, 'Actualizado correctamente');
            onCancel();
            await fetchData();
          }
        }
      } else {
        const clone = [...data];
        if (!key) {
          clone.push({
            ...values,
            key: clone.length + 1,
          });
          setData(clone);
          callback(clone);
          onCancel();
        } else {
          const res = clone.map((item) => (item.key === key ? { ...values, key } : item));
          setData(res);
          callback(res);
          onCancel();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (selectedRowKey) {
        if (key) {
          const response = await API.delete(`${baseURI}/${key}/`);
          if (response?.status === 204) {
            onSuccess(response, 'Eliminado correctamente');
            onCancel();
            await fetchData();
          }
        }
      } else {
        const clone = data.filter((item) => item.key !== key);
        const normalized = clone.map((item, idx) => ({ ...item, key: idx }));
        setData(normalized);
        callback(normalized);
        onCancel();
        setVisibleAlert(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickAdd = () => {
    setVisible(true);
    setSelectedRowKeys([]);
    formTable.resetFields();
  };

  const filter = (val) => {
    if (val) {
      const filtered = subCatFam.filter((e) => e.familia_de_tramite === val);
      setSubCatFamFiltered(filtered);
      formTable.resetFields(['sub_categoria_de_familia_de_tramite']);
    }
  };

  const onClickEdit = async () => {
    const [key] = selectedRowKeys;
    const clone = [...data];
    const match = clone.find((item) => item.id === key);
    filter(match.familia_de_tramite);
    setVisible(true);
    formTable.setFieldsValue(match);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    const _key = selectedRowKey ? record.id : record.key;
    setSelectedRowKeys([_key]);
  };
  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Familia del Tramite',
      dataIndex: 'familia_de_tramite',
      key: 'familia_de_tramite',
      width: 100,
      render: (val) => familia.find((item) => (item.key || item.id) === val)?.descripcion,
    },
    {
      titleText: 'Subcategoría de Familia del Trámite',
      dataIndex: 'sub_categoria_de_familia_de_tramite',
      key: 'sub_categoria_de_familia_de_tramite',
      width: 100,
      render: (val) => {
        const _subCategoria = val.map((e) => subCatFam.find((i) => i.id === e)?.descripcion);
        return _subCategoria.map((e) => ` ${e}`).toString();
      },
    },

  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            mobileColIndex={0}
            rowKey={selectedRowKey ? 'id' : 'key'}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            allowImport
            baseURI={baseURI}
          />
        ) : (
          <Card
            bordered={false}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
              />
             )}
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Familia del Trámite`}
          >
            <Form
              layout="vertical"
              form={formTable}
              onFinish={onFinish}
              scrollToFirstError
            >
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="familia_de_tramite"
                  label="Familia del Trámite"
                  hasFeedback
                >
                  <Select
                    dataSource={familia}
                    onChange={filter}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="sub_categoria_de_familia_de_tramite"
                  label="Subcategoría de Familia del Trámite"
                  hasFeedback
                >
                  <Select dataSource={subCatFamFiltered} mode="multiple" />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>

          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Familia del Trámite seleccionada"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};
TableFamiliaDelTramite.propTypes = {

  selectedRowKey: PropTypes.number,
  callback: PropTypes.func.isRequired,

};
TableFamiliaDelTramite.defaultProps = {
  selectedRowKey: null,
};

export default TableFamiliaDelTramite;
